import config from "environment";
import axios, { AxiosInstance, AxiosProgressEvent } from "axios";
import { v4 as uuidv4 } from "uuid";
import { loadState } from "../utils/localStorage";
import { ApiResponse, PolicyProjectType, UploadEmployeeFilesContract } from "winslow-core";

const { baseApi, clientUrl } = config;

const localState = loadState();

function buildBaseHeaders(
  contentType: "application/json" | "multipart/form-data"
) {
  if (localState?.appData?.token) {
    return {
      Accept: "application/json",
      "Content-Type": contentType,
      "x-client-token": uuidv4().toString(),
      "x-api-token": localState?.appData?.token || "",
    };
  } else {
    return {
      Accept: "application/json",
      "Content-Type": contentType,
      "x-client-token": uuidv4().toString(),
    };
  }
}

const createInstance = (
  contentType: "application/json" | "multipart/form-data"
): AxiosInstance => {
  return axios.create({
    baseURL: baseApi,
    headers: buildBaseHeaders(contentType),
  });
};

export const apiClient: AxiosInstance = createInstance("application/json");

export const uploadClient: AxiosInstance = createInstance(
  "multipart/form-data"
);

const basicXClientTokenInterceptor = (config: any) => {
  config.headers["x-client-token"] = uuidv4().toString();

  return config;
};

apiClient.interceptors.request.use(basicXClientTokenInterceptor);
uploadClient.interceptors.request.use(basicXClientTokenInterceptor);
export default apiClient;

export const setAxiosAuthToken = (token: string | undefined) => {
  const setAuthTokenHeaders = (config: any) => {
    config.headers["x-client-token"] = uuidv4().toString();
    config.headers["x-api-token"] = token;

    return config;
  };

  apiClient.interceptors.request.use(setAuthTokenHeaders);
  uploadClient.interceptors.request.use(setAuthTokenHeaders);
};

const notAuthorizedHandler = (error: any) => {
  const { status } = error.response;
  if (status === 401) {
    window.location.href = clientUrl + "/logout";
  }
  return Promise.reject(error);
};

apiClient.interceptors.response.use(
  (response) => response,
  notAuthorizedHandler
);
uploadClient.interceptors.response.use(
  (response) => response,
  notAuthorizedHandler
);

export const getRequest = async <R>(
  url: string,
  funcName: string
): Promise<R | null> => {
  try {
    const response = await apiClient.get(url);

    const result = response.data;

    const { error } = result;

    if (response.status === 500 || !result.data) {
      console.log(`[${funcName}] API call returned error: ` + error);
      return null as R;
    }

    return result.data as R;
  } catch (err) {
    console.log(`[${funcName}] API threw an exception: ` + err);
    return null as R;
  }
};

export const postRequest = async <P, R>(
  url: string,
  body: P,
  funcName: string
): Promise<R> => {
  try {
    const response = await apiClient.post(url, body);

    const result = response.data;

    const { error, data } = result;

    // console.log("[client] post respnose: ", result);

    if (response.status === 500 || !!!data) {
      console.log(`[${funcName}] API call returned and error: ` + error);
      return null as R;
    }

    return data as R;
  } catch (err) {
    console.log(`[${funcName}] API called threw and exception: ` + err);
  }

  return null as R;
};

export const patchRequest = async <P, R>(
  url: string,
  body: P,
  funcName: string
): Promise<R> => {
  try {
    const response = await apiClient.patch(url, body);

    const result = response.data;

    const { error, data } = result;

    if (response.status === 500 || !!!data) {
      console.log(`[${funcName}] API call returned and error: ` + error);
      return null as R;
    }

    return data as R;
  } catch (err) {
    console.log(`[${funcName}] API called threw and exception: ` + err);
  }

  return null as R;
};

export const putRequest = async <P, R>(
  url: string,
  body: P,
  funcName: string
): Promise<R> => {
  try {
    const response = await apiClient.put(url, body);

    const result = response.data;

    const { error, data } = result;

    if (response.status === 500 || !!!data) {
      console.log(`[${funcName}] API call returned and error: ` + error);
      return null as R;
    }

    return data as R;
  } catch (err) {
    console.log(`[${funcName}] API called threw and exception: ` + err);
  }

  return null as R;
};

export const deleteRequest = async <R>(
  url: string,
  funcName: string
): Promise<R> => {
  try {
    const response = await apiClient.delete(url);

    const result = response.data;
    const { error, data } = result;

    if (response.status === 500 || !data) {
      console.log(`[${funcName}] API call returned error: ` + error);
      return null as R;
    }

    return data as R;
  } catch (err) {
    console.log(`[${funcName}] API threw an exception: ` + err);
  }

  return null as R;
};

// NR> Check versions which need to return the whole API respnose object 
// as we need to tunnel the error messages back to the client
export const getCheckRequest = async <R>(
  url: string,
  funcName: string
): Promise<ApiResponse<R> | null> => {
  try {
    const response = await apiClient.get(url);
    const result = response.data;
    return result as ApiResponse<R>;

    // return await apiClient.get(url) as ApiResponse<R>;
  } catch (err) {
    console.log(`[${funcName}] API threw an exception: ` + err);
    return null
  }
};

export const postCheckRequest = async <P, R>(
  url: string,
  body: P,
  funcName: string
): Promise<ApiResponse<R> | null> => {
  try {
    const response = await apiClient.post(url, body);
    const result = response.data;
    return result as ApiResponse<R>;
    // return await apiClient.post(url, body) as ApiResponse<R>;
  } catch (err) {
    console.log(`[${funcName}] API threw an exception: ` + err);
    return null
  }
};

export const patchCheckRequest = async <P, R>(
  url: string,
  body: P,
  funcName: string
): Promise<ApiResponse<R> | null> => {
  try {
    const response = await apiClient.patch(url, body);
    const result = response.data;
    return result as ApiResponse<R>;
    // return await apiClient.patch(url, body) as ApiResponse<R>;
  } catch (err) {
    console.log(`[${funcName}] API threw an exception: ` + err);
    return null
  }
};

export const deleteCheckRequest = async <R>(
  url: string,
  funcName: string
): Promise<ApiResponse<R> | null> => {
  try {
    const response = await apiClient.delete(url);
    const result = response.data;
    return result as ApiResponse<R>;
    // return await apiClient.delete(url) as ApiResponse<R>;
  } catch (err) {
    console.log(`[${funcName}] API threw an exception: ` + err);
    return null
  }
};

export const uploadCheckRequest = async <R>(
  url: string,
  body: FormData,
  funcName: string
): Promise<ApiResponse<R> | null> => {
  try {
    return await uploadClient.post(url, body) as ApiResponse<R>;
  } catch (err) {
    console.log(`[${funcName}] API threw an exception: ` + err);
    return null
  }
};


export const postUpload = async (
  userId: string,
  file: Blob,
  duration: number,
  handleUploadProcess: (processEvent: AxiosProgressEvent) => void
) => {
  const formData = new FormData();
  formData.append("file", file);
  return await uploadClient.post(
    `${baseApi}/assets/capture/${userId}/${duration}`,
    formData,
    {
      onUploadProgress: handleUploadProcess,
    }
  );
};

export const postScreenshot = async (
  assetId: string,
  userId: string,
  frame: {
    blob: Blob;
    dataUri: any;
    format: any;
  },
  handleUploadProcess: (processEvent: AxiosProgressEvent) => void
) => {
  const formData = new FormData();
  formData.append("file", frame.blob);
  return await uploadClient.post(
    `${baseApi}/assets/screenshot/${assetId}/user/${userId}`,
    formData,
    {
      onUploadProgress: handleUploadProcess,
    }
  );
};
// this is more of a generic image uploader for things like avatars
// we assume the code has validated the type before hand
export const postImage = async (
  userId: string,
  file: File,
  handleUploadProcess: (processEvent: AxiosProgressEvent) => void
) => {
  const formData = new FormData();
  formData.append("file", file);

  // grab the image format
  const format = file.type ? file.type.split("/").pop() : "";

  if (format === "") {
    console.log("[client/postImage] bad image format: ", file.type);
    return;
  }

  return await uploadClient.post(
    `${baseApi}/files/upload-image/${userId}/${format}`,
    formData
  );
};

export const postFileUpload = async (
  userId: string,
  collectionId: string,
  file: File
) => {
  const formData = new FormData();
  formData.append("file", file);

  return await uploadClient.post(
    `${baseApi}/files/upload-files/${userId}/${collectionId}`,
    formData
  );
};

export const postMultipleFileUpload = async (
  userId: string,
  collectionId: string,
  files: FileList
) => {
  const formData = new FormData();

  for (let i = 0; i < files.length; i++) {
    formData.append("file", files[i]);
  }

  return await uploadClient.post(
    `${baseApi}/files/upload-files/${userId}/${collectionId}`,
    formData
  );
};

export const postMultipleEmployeeFileUpload = async (
  workspaceId: string,
  files: FileList
) => {
  const formData = new FormData();

  for (let i = 0; i < files.length; i++) {
    formData.append("file", files[i]);
  }

  return await uploadClient.post(
    `${baseApi}/files/upload-employee-files/${workspaceId}}`,
    formData
  );
};

export const apiDeleteEmployeeRecordFileFromCloud = async (
  employeeRecordId: string,
  cloudURL: string
) => {
  return await apiClient.post(`${baseApi}/files/delete-employee-record-file/${employeeRecordId}`, 
    { cloudURL }
  );
};

export const postPolicyStudioFileUpload = async (
  userId: string,
  file: File,
  type: PolicyProjectType
) => {
  const formData = new FormData();
  formData.append("file", file);

  return await uploadClient.post(
    `${baseApi}/policy-studio/upload-file/${userId}/${type}`,
    formData
  );
};

export const postPublicPolicyStudioFileUpload = async (
  file: File,
  type: PolicyProjectType,
  email: string | null
) => {
  const formData = new FormData();
  formData.append("file", file);

  return await uploadClient.post(
    `${baseApi}/policy-studio/public/upload-file/${type}?email=${email}`,
    formData
  );
};
